import ComparisonChip from '@precis-digital/kurama/ComparisonChip'
import { type Theme, type ThemeColor } from '@precis-digital/kurama/theme'
import { type PercentageStatus, calDifferenceRelativeToPrevious } from 'shared/utils'
import { formatToPercent } from 'shared/numberFormat'

export interface ChipColorTheme {
  tokens: Theme['tokens']
}

export const getChipColorsByStatus = ({
  theme,
  differenceIndicator,
  reverseColorScheme = false
}: {
  theme: ChipColorTheme
  differenceIndicator: PercentageStatus
  reverseColorScheme?: boolean
}): { backgroundColor: string; color: string } => {
  if (differenceIndicator === 'equal') {
    return {
      backgroundColor: theme.tokens.background.tonal.soft.default,
      color: theme.tokens.foreground.main.secondary
    }
  }
  const isSuccess = reverseColorScheme ? differenceIndicator === 'negative' : differenceIndicator === 'positive'
  return {
    backgroundColor: isSuccess
      ? theme.tokens.background.semantic.success.tonal.superSoft.default
      : theme.tokens.background.semantic.error.tonal.superSoft.default,
    color: isSuccess
      ? theme.tokens.background.semantic.success.main.default
      : theme.tokens.background.semantic.error.main.default
  }
}

interface PercentageChipProps {
  presentValue: number
  previousValue: number
  reverseColorScheme?: boolean
  /** When 'neutral', chip uses black background and white text (Figma-style) regardless of increase/decrease */
  colorVariant?: 'semantic' | 'neutral'
}

export const getTextColorByDifferenceIndicatorAndColorScheme = ({
  theme,
  differenceIndicator,
  reverseColorScheme
}: {
  theme: Theme
  differenceIndicator: PercentageStatus
  reverseColorScheme: boolean
}): string => {
  let color: ThemeColor = theme.tokens.foreground.main.secondary
  switch (differenceIndicator) {
    case 'positive':
      color = !reverseColorScheme
        ? theme.tokens.background.semantic.success.main.default
        : theme.tokens.background.semantic.error.main.default
      break
    case 'negative':
      color = !reverseColorScheme
        ? theme.tokens.background.semantic.error.main.default
        : theme.tokens.background.semantic.success.main.default
      break
  }

  return color
}

const getPercentageDifference = (presentValue: number, previousValue: number): number => {
  if (previousValue === 0 && presentValue !== 0) {
    return presentValue > 0 ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY
  }

  return calDifferenceRelativeToPrevious(presentValue, previousValue)
}

export const PercentageChip = ({
  presentValue,
  previousValue,
  reverseColorScheme = false
}: PercentageChipProps): React.ReactElement => {
  const percentageDifference = getPercentageDifference(presentValue, previousValue)

  return (
    <ComparisonChip
      value={percentageDifference}
      positiveIsGood={!reverseColorScheme}
      labelFormatter={formatToPercent}
    />
  )
}

export default PercentageChip
